import {
  Box,
  Center,
  Grid,
  Group,
  Pagination,
  ScrollArea,
  Select,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core'
import { currencyFormatter } from '@ospace/common'
import { omitKeys } from '@ospace/common/utils/object'
import { parseDate, sortDates } from '@ospace/shared'
import { IconChevronDown, IconChevronUp, IconSearch, IconSelector } from '@tabler/icons-react'
import { useMemo, useState } from 'react'
import * as React from 'react'

import { CSVExportButton } from '../CSVExportButton'
import classes from './style.module.css'
import { ColDef, NewTableProps, ThProps } from './types'

type PayloadType = {
  sortBy: string | null
  reversed: boolean
  search: string
  colDef?: ColDef
}

type SortRow = { [key: string]: unknown }

// Pagination Hook
const usePagination = (tableData: any[], initialItemsPerPage: number) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage)

  const total = Math.ceil(tableData.length / itemsPerPage)

  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage

  const currentPageData = tableData.slice(startIndex, endIndex)

  const handleItemsPerPageChange = (value: string | null) => {
    if (!value) return

    setItemsPerPage(Number(value))
    setCurrentPage(1)
  }

  return {
    currentPage,
    setCurrentPage,
    total,
    currentPageData,
    itemsPerPage,
    setItemsPerPage,
    handleItemsPerPageChange,
  }
}

// Filter data based on search query
const filterData = (data: any[], search: string) => {
  if (!data.length || !search.trim()) {
    return data
  }

  const query = search.toLowerCase().trim()

  return data.filter((item: any) =>
    Object.keys(data[0]).some((key) => item[key]?.toString()?.toLowerCase()?.includes(query))
  )
}

// Sort data based on payload
const sortData = (data: any[], payload: PayloadType) => {
  const filteredData = filterData(data, payload.search)

  if (payload.sortBy === null) {
    return filteredData
  }

  return sortPayload(filteredData, payload)
}

// sortPayload implements a sort for payloads on the NewTable. It is exported
// so it can be unit tested
export const sortPayload = (data: any[], payload: PayloadType) => {
  const sortBy = payload.sortBy!

  if (payload?.colDef?.dataType === 'date') {
    return [...data].sort((a: any, b: any) =>
      payload.reversed
        ? sortDates(a[sortBy], b[sortBy], payload?.colDef?.dateFormat)
        : sortDates(b[sortBy], a[sortBy], payload?.colDef?.dateFormat)
    )
  }

  if (payload?.colDef?.dataType === 'number' || payload?.colDef?.dataType === 'currency') {
    return [...data].sort((aRow: SortRow, bRow: SortRow) => {
      const a = Number(aRow[sortBy]) || 0
      const b = Number(bRow[sortBy]) || 0

      return payload.reversed ? a - b : b - a
    })
  }

  return [...data].sort((aRow: SortRow, bRow: SortRow) => {
    const a = typeof aRow[sortBy] === 'string' ? (aRow[sortBy] as string) : ''

    const b = typeof bRow[sortBy] === 'string' ? (bRow[sortBy] as string) : ''

    return payload.reversed ? b.localeCompare(a) : a.localeCompare(b)
  })
}

// Table Header Component
const Th = ({ children, reversed, sorted, onSort, style }: ThProps) => {
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector

  return (
    <th className={classes.th} style={style}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group justify='space-between'>
          <Text fw={600} c='gray' fz='md'>
            {children}
          </Text>

          <Center className={classes.icon}>
            <Icon size='0.9rem' stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </th>
  )
}

export const NewTable = ({
  enableExportButton = false,
  exportData,
  colDef,
  tableData,
  tableProps,
  modal,
  hideSearch = false,
  hidePagination = false,
  hideHeader = false,
  filter,
  title = '',
  vendorSalesPersonFilter,
  fixFirstColumn = false,
}: NewTableProps) => {
  const rowKey = tableProps?.rowKey || 'id'

  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<string | null>(null)
  const [reverseSortDirection, setReverseSortDirection] = useState(false)

  /**
   * IMPORTANT:
   * Do not store sortedData in state.
   *
   * tableData can change when an action happens in the parent.
   * If we store sortedData and reset it whenever tableData changes,
   * search/sort/filter state gets lost.
   *
   * Instead, derive the displayed data from:
   * tableData + search + sorting
   */
  const processedData = useMemo(() => {
    return sortData(tableData, {
      sortBy,
      reversed: reverseSortDirection,
      search,
      colDef: sortBy ? colDef.find((def) => def.key === sortBy) : undefined,
    })
  }, [tableData, search, sortBy, reverseSortDirection, colDef])

  const {
    currentPage,
    setCurrentPage,
    currentPageData,
    total,
    itemsPerPage,
    handleItemsPerPageChange,
  } = usePagination(processedData, tableProps?.itemsPerPage || 10)

  const pageSizeOptions = useMemo(
    () =>
      [...new Set([tableProps?.itemsPerPage ?? 10, 10, 15, 25, 50, 100])]
        .sort((a, b) => a - b)
        .map(String),
    [tableProps?.itemsPerPage]
  )

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget

    setCurrentPage(1)
    setSearch(value)
  }

  const setSorting = (field: string) => {
    const reversed = field === sortBy ? !reverseSortDirection : false

    setCurrentPage(1)
    setReverseSortDirection(reversed)
    setSortBy(field)
  }

  const handleRowClick = (row: any) => {
    modal && modal.setModalData(row)
    modal && modal.open()
  }

  const getCellContent = (col: ColDef, element: any) => {
    if (col.element) {
      return col.element(element)
    }

    if (col.dataType === 'currency') {
      const value = Math.round(element[col.key] * 100) / 100

      return `${col.currency} ${currencyFormatter().format(value)}`
    }

    if (col.dataType === 'date') {
      return parseDate(element[col.key], col.dateFormat)
    }

    return element[col.key]
  }

  return (
    <Box p='md'>
      <Grid grow>
        <Grid.Col span={3}>
          <Text c='dimmed' fw={600} fz='14px' tt='uppercase'>
            {title}
          </Text>
        </Grid.Col>

        <Grid.Col span={9}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '10px',
            }}
          >
            {!hideSearch && (
              <TextInput
                placeholder='Search'
                mb='md'
                leftSection={<IconSearch size='0.9rem' stroke={1.5} />}
                value={search}
                onChange={handleSearchChange}
                w='60%'
              />
            )}

            <div style={{ display: 'flex' }}>
              {enableExportButton && exportData && (
                <CSVExportButton data={exportData} fileName={title} />
              )}

              <div
                style={{
                  position: 'relative',
                  top: '-20px',
                }}
              >
                {vendorSalesPersonFilter}
              </div>
            </div>
          </div>
        </Grid.Col>
      </Grid>

      {filter &&
        filter(
          [...tableData],
          (data: any[]) => {
            // This callback is retained for compatibility
            // with the existing filter API.
            setCurrentPage(1)
          },
          setCurrentPage
        )}

      <ScrollArea>
        <Table highlightOnHover {...omitKeys(tableProps || {}, ['rowKey', 'itemsPerPage'])}>
          {!hideHeader && (
            <thead>
              <tr key='header'>
                {colDef.map((th) => {
                  const isFirstColumn = colDef[0].key === th.key

                  return (
                    <Th
                      sorted={sortBy === th.key}
                      reversed={reverseSortDirection}
                      onSort={() => setSorting(th.key)}
                      key={th.key}
                      style={{
                        position: fixFirstColumn && isFirstColumn ? 'sticky' : 'static',

                        left: fixFirstColumn && isFirstColumn ? 0 : 'auto',

                        backgroundColor:
                          fixFirstColumn && isFirstColumn ? '#f9f9f9' : 'transparent',

                        zIndex: fixFirstColumn && isFirstColumn ? 2 : undefined,
                      }}
                    >
                      {th.label}
                    </Th>
                  )
                })}
              </tr>
            </thead>
          )}

          <tbody>
            {currentPageData?.map((element: any) => (
              <tr
                key={element[rowKey] || element.oppNumber}
                onClick={() => handleRowClick(element)}
              >
                {colDef.map((col) => {
                  const isFirstColumn = colDef[0].key === col.key

                  return (
                    <td
                      key={col.key}
                      style={{
                        maxWidth: col.width || '200px',

                        minWidth: col.width || '',

                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        paddingRight: 40,
                        cursor: modal ? 'pointer' : 'auto',

                        padding: '8px',
                        borderBottom: '1px solid #e0e0e0',

                        fontSize: 12,

                        position: fixFirstColumn && isFirstColumn ? 'sticky' : 'static',

                        left: fixFirstColumn && isFirstColumn ? 0 : 'auto',

                        backgroundColor:
                          fixFirstColumn && isFirstColumn ? '#f9f9f9' : 'transparent',

                        zIndex: fixFirstColumn && isFirstColumn ? 1 : undefined,
                      }}
                    >
                      {getCellContent(col, element)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollArea>

      {!hidePagination && (
        <Center m='xl'>
          <Group justify='space-between' m='xl'>
            <Group gap='xs'>
              <Text size='sm' c='dimmed'>
                Items per page
              </Text>

              <Select
                value={String(itemsPerPage)}
                onChange={handleItemsPerPageChange}
                data={pageSizeOptions}
                w={80}
                size='sm'
                allowDeselect={false}
              />
            </Group>

            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={total}
              withControls
              withEdges
              withPages
            />
          </Group>
        </Center>
      )}

      {modal && modal.component}
    </Box>
  )
}
